import React, { useCallback, useRef, useState } from 'react';
import { PCT_MAX, autoPbMax } from './speechLogic';

const RE_COLOR = '#dc2626';
const LE_COLOR = '#2563eb';

/**
 * PbMaxBarGraph — two-bar chart for Speech Discrimination (PB Max).
 *
 * • X = two categorical columns (Right Ear · Left Ear)
 * • Y = Score (%), 0..100 (top → 100, bottom → 0)
 * • Bars auto-populate from `autoPbMax(state, ear)` (WRS peak). The
 *   `pb_max.right / .left` override is set by dragging the bar OR
 *   typing into the table row. Clearing the override falls back to
 *   the auto value.
 */
const PbMaxBarGraph = ({
  state,           // full normalised speech state
  onSetValue,     // (ear, percent) => void
  readOnly = false,
  width = 460, height = 240,
}) => {
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null);
  const [dragging, setDragging] = useState(null);
  const marginL = 46, marginR = 12, marginT = 22, marginB = 40;
  const plotW = width - marginL - marginR;
  const plotH = height - marginT - marginB;
  const cols = [
    { key: 'right', label: 'Right Ear (RE)', color: RE_COLOR },
    { key: 'left',  label: 'Left Ear (LE)',  color: LE_COLOR },
  ];
  const colX = (idx) => marginL + plotW * (idx + 0.5) / cols.length;
  const yScale = useCallback((pct) => marginT + ((PCT_MAX - pct) * plotH) / PCT_MAX, [plotH]);
  const yInv = useCallback((py) => {
    const raw = PCT_MAX - ((py - marginT) * PCT_MAX) / plotH;
    return Math.max(0, Math.min(PCT_MAX, Math.round(raw / 2) * 2));
  }, [plotH]);

  const toViewBox = (evt) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX; pt.y = evt.clientY;
    const p = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x: p.x, y: p.y };
  };
  const snap = (p) => {
    if (!p) return null;
    if (p.y < marginT - 4 || p.y > height - marginB + 4) return null;
    if (p.x < marginL - 6 || p.x > width - marginR + 6) return null;
    let bestIdx = 0, bestDx = Infinity;
    cols.forEach((_, i) => {
      const dx = Math.abs(colX(i) - p.x);
      if (dx < bestDx) { bestDx = dx; bestIdx = i; }
    });
    return { idx: bestIdx, ear: cols[bestIdx].key, pct: yInv(Math.max(marginT, Math.min(height - marginB, p.y))) };
  };

  const handleMouseDown = (evt) => {
    if (readOnly) return;
    const s = snap(toViewBox(evt));
    if (!s) return;
    setDragging(s.ear);
    onSetValue && onSetValue(s.ear, s.pct);
  };
  const handleMouseMove = (evt) => {
    const s = snap(toViewBox(evt));
    setHover(s);
    if (readOnly || !dragging) return;
    if (s && s.ear === dragging) onSetValue && onSetValue(dragging, s.pct);
  };
  const stopDrag = () => setDragging(null);

  const yTicks = [0, 20, 40, 60, 80, 100];

  return (
    <div className="w-full">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        width="100%" height={height}
        preserveAspectRatio="none"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={stopDrag}
        onMouseLeave={() => { stopDrag(); setHover(null); }}
        data-testid="pbmax-graph"
        style={{ cursor: readOnly ? 'default' : 'ns-resize', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: 4, display: 'block', userSelect: 'none' }}
      >
        {yTicks.map((t) => (
          <g key={t}>
            <line x1={marginL} x2={width - marginR} y1={yScale(t)} y2={yScale(t)} stroke="#e2e8f0" strokeWidth="0.5" />
            <text x={marginL - 6} y={yScale(t) + 3} fontSize="9" fill="#64748b" textAnchor="end">{t}</text>
          </g>
        ))}
        <text x={marginL - 34} y={marginT + plotH / 2 - 10} fontSize="9" fill="#475569" transform={`rotate(-90, ${marginL - 34}, ${marginT + plotH / 2})`} textAnchor="middle">Score (%)</text>
        {cols.map((c, i) => {
          const cx = colX(i);
          const auto = autoPbMax(state, c.key);
          const override = state.pb_max?.[c.key];
          const val = (override !== null && override !== undefined) ? Number(override) : auto;
          const shown = val !== null && val !== undefined && Number.isFinite(Number(val));
          const barTop = shown ? yScale(Number(val)) : yScale(0);
          const barBottom = yScale(0);
          const barW = plotW / 4;
          return (
            <g key={c.key} data-testid={`pbmax-bar-${c.key}`}>
              {shown && (
                <rect x={cx - barW * 0.30} y={barTop} width={barW * 0.60} height={Math.max(0, barBottom - barTop)} fill={c.color} rx="2" />
              )}
              {!shown && (
                <rect x={cx - barW * 0.30} y={barBottom - 2} width={barW * 0.60} height="2" fill="#cbd5e1" />
              )}
              {shown && (
                <>
                  <rect x={cx - 34} y={Math.max(marginT + 2, barTop - 22)} width="68" height="20" rx="4" fill="#ffffff" stroke={c.color} strokeWidth="1.25" />
                  <text x={cx} y={Math.max(marginT + 15, barTop - 7)} fontSize="12" fill={c.color} textAnchor="middle" fontWeight="800" data-testid={`pbmax-bar-label-${c.key}`}>{`${val} %`}</text>
                </>
              )}
              <text x={cx} y={height - marginB + 16} fontSize="10" fill={c.color} textAnchor="middle" fontWeight="700">
                {c.label}
              </text>
            </g>
          );
        })}
        {!readOnly && hover && (
          <g style={{ pointerEvents: 'none' }} data-testid="pbmax-hover">
            <line x1={colX(hover.idx)} x2={colX(hover.idx)} y1={marginT} y2={height - marginB} stroke={cols[hover.idx].color} strokeWidth="0.6" strokeDasharray="2 2" opacity="0.5" />
            <rect x={colX(hover.idx) + 6} y={Math.max(marginT, yScale(hover.pct) - 10)} width="42" height="14" rx="2" fill="#0f172a" opacity="0.85" />
            <text x={colX(hover.idx) + 8} y={Math.max(marginT, yScale(hover.pct) - 10) + 10} fontSize="9" fill="#ffffff" fontWeight="600">
              {hover.pct}%
            </text>
          </g>
        )}
      </svg>
    </div>
  );
};

export default PbMaxBarGraph;
